import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { Icon } from '@iconify/react';
import { Typography, Button, Modal, InputBase, IconButton } from '@mui/material';
import axiosInstance from 'src/api/axiosInstance';
import { useSnackbar } from 'src/components/snackbar';
import { useRouter } from 'src/routes/hooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paths } from 'src/routes/paths';
import Logo from 'src/components/logo';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { encryptData, decryptData } from '../../utils/Crypto';

export default function CandidateFormLayout({ children }) {
  const [email, setEmail] = useState('');
  const [otpArray, setOtpArray] = useState(Array(6).fill(''));
  const inputRefs = useRef([]);
  const [showOtpField, setShowOtpField] = useState(false);
  const [modalOpen, setModalOpen] = useState(true);
  const [otpError, setOtpError] = useState('');
  const [searchParams] = useSearchParams();
  const [remainingTime, setRemainingTime] = useState(0);
  const [blockEndTime, setBlockEndTime] = useState(null);
  const [sendDisabled, setSendDisabled] = useState(false);
  const [isPermanentBlock, setIsPermanentBlock] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  const [isAlreadySubmitted, setIsAlreadySubmitted] = useState(false);

  const prefilledEmail = searchParams.get('email');
  const expParam = searchParams.get('exp');
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(
    () => {
      let timer;

      // Check link expiration if exp parameter is present
      let isExpired = false;
      if (expParam) {
        try {
          const decryptedExp = decryptData(expParam);
          if (decryptedExp && !Number.isNaN(Number(decryptedExp))) {
            const expiryTime = Number(decryptedExp);
            if (Date.now() > expiryTime) {
              setIsLinkExpired(true);
              setModalOpen(true);
              isExpired = true;
            }
          }
        } catch (e) {
          // ignore
        }
      }

      if (!isExpired) {
        if (prefilledEmail) {
          let cleanEmail = prefilledEmail;
          try {
            const decrypted = decryptData(prefilledEmail);
            if (decrypted) {
              cleanEmail = decrypted;
            }
          } catch (e) {
            // not encrypted or already plain email
          }
          setEmail(cleanEmail);

          // Check if candidate already submitted the form
          const normalizedEmail = cleanEmail ? cleanEmail.toLowerCase().trim() : '';
          if (normalizedEmail && localStorage.getItem(`cand_submitted_${normalizedEmail}`) === 'true') {
            setIsAlreadySubmitted(true);
          }

          setModalOpen(true);
        } else {
          setModalOpen(false);
        }
      }

      return () => {
        if (timer) clearTimeout(timer);
      };
    },
    [prefilledEmail, expParam]
  );

  useEffect(() => {
    if (!email || isLinkExpired || isAlreadySubmitted) return;
    const checkBlockStatus = async () => {
      try {
        const res = await axiosInstance.get(`/rest/candidate/otp/check-block-status`, {
          params: { email },
        });
        const { blocked, secondsRemaining, permanentBlock } = res.data;
        if (permanentBlock) {
          setIsPermanentBlock(true);
          setSendDisabled(true);
          setOtpError('You are permanently blocked. Contact administrator.');
          setBlockEndTime(null);
        } else if (blocked && secondsRemaining > 0) {
          setIsPermanentBlock(false);
          setSendDisabled(true);
          setBlockEndTime(Date.now() + secondsRemaining * 1000);
          setOtpError('You are temporarily blocked. Please wait.');
        } else {
          setIsPermanentBlock(false);
          setSendDisabled(false);
          setOtpError('');
          setBlockEndTime(null);
        }
      } catch (err) {
        console.error('Failed to check block status:', err);
      }
    };
    checkBlockStatus();
  }, [email, isLinkExpired, isAlreadySubmitted]);

  useEffect(() => {
    if (!blockEndTime) return undefined;
    const updateRemaining = () => {
      const diff = Math.max(0, Math.floor((blockEndTime - Date.now()) / 1000));
      setRemainingTime(diff);
      if (diff === 0) {
        clearInterval(timerRef.current);
        setBlockEndTime(null);
        setOtpError('');
        setSendDisabled(false);
        setIsPermanentBlock(false);
      }
    };
    updateRemaining();
    timerRef.current = setInterval(updateRemaining, 1000);
    return () => clearInterval(timerRef.current);
  }, [blockEndTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`;
  };

  const handleSendOtp = async (emailToSend = email) => {
    setOtpError('');
    setIsLoading(true);
    if (isPermanentBlock) {
      setIsLoading(false);
      return;
    }
    try {
      await axiosInstance.post(`/rest/candidate/otp/send`, { email: emailToSend });
      setShowOtpField(true);
      setOtpArray(Array(6).fill(''));
      enqueueSnackbar('OTP sent successfully!', { variant: 'success' });
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setOtpError('No candidate found with this email address.');
      } else if (status === 429) {
        const backendMessage = err.response?.data?.error || '';
        const waitSeconds = parseInt(err.response.data.secondsToWait || '60', 10);
        setBlockEndTime(Date.now() + waitSeconds * 1000);
        setSendDisabled(true);
        if (backendMessage.toLowerCase().includes('temporarily blocked')) {
          setOtpError('You have been temporarily blocked due to multiple requests.');
        } else if (backendMessage.toLowerCase().includes('must wait')) {
          setOtpError('Please wait a moment before requesting a new OTP.');
        } else {
          setOtpError('Too many attempts detected. Please try again later.');
        }
      } else if (status === 403) {
        setOtpError(
          'Your account has been permanently blocked. Please contact the administrator for assistance.'
        );
        setIsPermanentBlock(true);
        setSendDisabled(true);
      } else {
        enqueueSnackbar('Unable to send OTP at the moment. Please try again later.', {
          variant: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const fullOtp = otpArray.join('');
    if (fullOtp.length !== 6) {
      setOtpError('Please enter complete OTP');
      return;
    }
    setIsLoading(true);
    try {
      const encryptedEmail = encryptData(email);
      const encryptedOtp = encryptData(fullOtp);
      const res = await axiosInstance.post(`/rest/candidate/otp/verify`, {
        encryptedEmail,
        encryptedOtp,
      });
      if (res.data) {
        enqueueSnackbar('OTP Verified Successfully!', { variant: 'success' });
        setOtpArray(Array(6).fill(''));
        try {
          const candidateRes = await axiosInstance.get(
            `/rest/candidate/otp/get-candidate-by-email`,
            {
              params: { email: encryptedEmail },
              withCredentials: false,
            }
          );
          if (candidateRes.data?.publicId) {
            router.push(paths.candeditForm(candidateRes.data.publicId));
          }
        } catch (err) {
          console.error('Error fetching candidate:', err);
        }
        setModalOpen(false);
      } else {
        enqueueSnackbar('Invalid OTP', { variant: 'error' });
      }
    } catch (err) {
      console.error('OTP verification failed:', err);
      enqueueSnackbar('OTP verification failed', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otpArray];
    newOtp[index] = value;
    setOtpArray(newOtp);
    setOtpError('');
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otpArray[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').trim();
    const digits = pasted.split('').filter((ch) => /\d/.test(ch));
    if (digits.length === otpArray.length) {
      setOtpArray(digits);
      setOtpError('');
    }
  };

  const handleResendOtp = () => {
    setOtpArray(Array(6).fill(''));
    setOtpError('');
    handleSendOtp();
  };

  const getButtonLabel = () => {
    if (isLoading) return showOtpField ? 'Verifying...' : 'Sending...';
    if (isPermanentBlock) return 'Permanently Blocked';
    if (remainingTime > 0) return `Please Wait ${formatTime(remainingTime)}`;
    return showOtpField ? 'Verify Code' : 'Send Verification Code';
  };

  const renderModalContent = () => {
    if (isLinkExpired) {
      return (
        <Box>
          {/* Decorative top accent bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 5,
              background: 'linear-gradient(90deg, #EF4444, #F97316)',
              borderRadius: '24px 24px 0 0',
            }}
          />

          <Box sx={{ textAlign: 'center', pt: 3.5, pb: 1.5, px: 0.5 }}>
            {/* Layered Icon Badge */}
            <Box
              sx={{
                position: 'relative',
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Outer ripple */}
              <Box
                sx={{
                  position: 'absolute',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid rgba(239, 68, 68, 0.18)',
                }}
              />
              {/* Mid ring */}
              <Box
                sx={{
                  position: 'absolute',
                  width: 62,
                  height: 62,
                  borderRadius: '50%',
                  bgcolor: 'rgba(239, 68, 68, 0.12)',
                  border: '1.5px solid rgba(239, 68, 68, 0.28)',
                }}
              />
              {/* Core icon */}
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 20px -5px rgba(239, 68, 68, 0.45)',
                  zIndex: 1,
                }}
              >
                <Icon icon="solar:clock-circle-bold" width={26} height={26} />
              </Box>
            </Box>

            {/* Status pill */}
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  px: 1.5,
                  py: 0.4,
                  borderRadius: '20px',
                  bgcolor: 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  color: '#DC2626',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: '#EF4444',
                  }}
                />
                Link Expired
              </Box>
            </Box>

            {/* Heading */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#111827',
                letterSpacing: '-0.025em',
                lineHeight: 1.25,
                mb: 1.25,
              }}
            >
              Your Link Has Expired
            </Typography>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: '#6B7280',
                lineHeight: 1.7,
                fontSize: '0.875rem',
                mb: 3,
                px: { xs: 0, sm: 0.5 },
              }}
            >
              This invitation link has expired and is no longer valid. Please reach out to your recruiter or the HR team to request a new link.
            </Typography>


          </Box>
        </Box>
      );
    }

    if (isAlreadySubmitted) {
      return (
        <Box sx={{ textAlign: 'center', py: 1.5, px: 0.5 }}>
          {/* Top Decorative Success Icon with layered pulse */}
          <Box
            sx={{
              position: 'relative',
              width: 72,
              height: 72,
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'rgba(16, 185, 129, 0.12)',
                border: '1.5px solid rgba(16, 185, 129, 0.25)',
              }}
            />
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 16px -4px rgba(16, 185, 129, 0.4)',
                zIndex: 1,
              }}
            >
              <Icon icon="solar:check-circle-bold" width={30} height={30} />
            </Box>
          </Box>

          {/* Status Badge */}
          <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.5,
                borderRadius: '20px',
                bgcolor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                color: '#047857',
                fontSize: '0.75rem',
                fontWeight: 700,

              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#10B981',
                  boxShadow: '0 0 6px #10B981',
                }}
              />
              Under Review
            </Box>
          </Box>

          {/* Heading */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#111827',
              letterSpacing: '-0.02em',
              mb: 1,
            }}
          >
            Application Already Submitted
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              color: '#4B5563',
              lineHeight: 1.65,
              fontSize: '0.9rem',
              mb: 3,
              px: { xs: 0, sm: 1 },
            }}
          >
            You have already submitted your application. Your profile details are currently under review by our recruitment team. Please wait for the recruiter to take further action.
          </Typography>

          {/* Informational Callout Card */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
              border: '1px solid #E2E8F0',
              borderLeft: '4px solid #10B981',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
            }}
          >
            <Icon
              icon="solar:info-circle-bold-duotone"
              width={22}
              height={22}
              color="#059669"
              style={{ flexShrink: 0, marginTop: 1 }}
            />
            <Box>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'block', lineHeight: 1.5, fontSize: '0.8rem' }}>
                Need to make any updates or have queries? Please contact our recruitment team.
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              bgcolor: '#e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1,
            }}
          >
            <Icon
              icon={showOtpField ? 'mdi:shield-key-outline' : 'mdi:email-lock'}
              width={30}
              height={30}
            />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {showOtpField ? 'Verify Your Identity' : 'OTP Verification'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {showOtpField
              ? 'Enter the 6-digit code sent to your email'
              : "We'll send a secure code to confirm your identity"}
          </Typography>
        </Box>

        {/* Email Display */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Email Address
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="mdi:email" width={20} height={20} color="#555" />
            <Typography variant="body2" fontWeight={500}>
              {email}
            </Typography>
          </Box>
        </Box>

        {/* OTP / Send OTP */}
        {!showOtpField ? (
          <>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => handleSendOtp()}
              disabled={sendDisabled || remainingTime > 0 || isPermanentBlock || isLoading}
              sx={{ textTransform: 'none', py: 1.5, borderRadius: 1 }}
            >
              {getButtonLabel()}
            </Button>

            {otpError && (
              <Box
                sx={{
                  backgroundColor:
                    otpError.toLowerCase().includes('Please wait') ||
                      otpError.toLowerCase().includes('temporarily blocked')
                      ? '#ebb11dff'
                      : '#eea0a2ff',
                  color: 'black',
                  borderRadius: 1,
                  p: 1,
                  mt: 1,
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  border: '1px solid',
                  borderColor:
                    otpError.toLowerCase().includes('wait') ||
                      otpError.toLowerCase().includes('temporarily')
                      ? '#ffcc80'
                      : '#ef9a9a',
                }}
              >
                {otpError}
              </Box>
            )}
          </>
        ) : (
          <>
            {/* OTP Input */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 2 }}>
              {otpArray.map((digit, index) => (
                <InputBase
                  key={index}
                  inputRef={(el) => {
                    if (el) inputRefs.current[index] = el;
                  }}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      width: '100%',
                      height: '50px',
                      fontSize: '1.2rem',
                      textAlign: 'center',
                      border: '1px solid #ccc',
                      borderRadius: 8,
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                  sx={{ flex: 1 }}
                />
              ))}
            </Box>

            {otpError && (
              <Box
                sx={{
                  backgroundColor:
                    otpError.toLowerCase().includes('Please wait') ||
                      otpError.toLowerCase().includes('temporarily blocked')
                      ? '#ebb11dff'
                      : '#eea0a2ff',
                  color: 'black',
                  borderRadius: 1,
                  p: 1,
                  mt: 1,
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  border: '1px solid',
                  borderColor:
                    otpError.toLowerCase().includes('wait') ||
                      otpError.toLowerCase().includes('temporarily')
                      ? '#ffcc80'
                      : '#ef9a9a',
                }}
              >
                {otpError}
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleVerifyOtp}
              disabled={isLoading || otpArray.join('').length !== 6}
              sx={{ textTransform: 'none', py: 1.5, mb: 1, mt: 1, borderRadius: 1 }}
            >
              {getButtonLabel()}
            </Button>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                mt: 2,
              }}
            >
              <Typography component="span" sx={{ color: '#333', mr: 0.5 }}>
                Didn’t receive code?
              </Typography>

              <Typography
                component="span"
                sx={{
                  color: 'green',
                  fontWeight: 600,
                  cursor:
                    sendDisabled || remainingTime > 0 || isLoading
                      ? 'not-allowed'
                      : 'pointer',
                  opacity: sendDisabled || remainingTime > 0 || isLoading ? 0.6 : 1,
                  textDecoration: 'underline',
                }}
                onClick={
                  sendDisabled || remainingTime > 0 || isLoading ? undefined : handleResendOtp
                }
              >
                {remainingTime > 0
                  ? `Resend Code (${formatTime(remainingTime)})`
                  : 'Resend Code'}
              </Typography>
            </Box>
          </>
        )}
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Box>
        <Logo sx={{ width: 160, height: 'auto', m: '20px 0 10px 25px' }} />

        {/* Candidate Application Header + Instructions */}
        <Box
          sx={{
            maxWidth: '1100px',
            mx: 'auto',
            border: '1px solid #e2e8f0',
            borderRadius: 3,
            mb: 1,
            backgroundColor: '#ffffff',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              minHeight: 88,
              px: { xs: 2, sm: 3 },
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              background:
                'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 1.8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                background:
                  'linear-gradient(135deg, #00a67d 0%, #008fb8 100%)',
                boxShadow: '0 4px 10px rgba(0, 150, 120, 0.20)',
              }}
            >
              <Icon
                icon="mdi:account-box-outline"
                width={27}
                height={27}
                color="#ffffff"
              />
            </Box>

            {/* Title */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#1e293b',
                  fontSize: '18px',
                  lineHeight: 1.3,
                }}
              >
                Candidate Application Form
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '13px',
                  mt: 0.3,
                }}
              >
                Complete your profile and application details
              </Typography>
            </Box>
          </Box>


        </Box>

        {/* Modal */}
        {(email || isLinkExpired || isAlreadySubmitted) && (
          <Modal
            open={modalOpen}
            sx={{
              backdropFilter: 'blur(8px)',
              bgcolor: 'rgba(15, 23, 42, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: { xs: '92%', sm: 490 },
                bgcolor: '#ffffff',
                borderRadius: '24px',
                p: { xs: 3, sm: 4 },
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
                outline: 'none',
                position: 'relative',
              }}
            >
              {renderModalContent()}
            </Box>
          </Modal>
        )}

        <Box component="main">{children}</Box>
      </Box>
    </Box>
  );
}

CandidateFormLayout.propTypes = {
  children: PropTypes.node,
};
